import React, { useMemo } from 'react';
import { URLS } from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import {
    useApiSearchAdminProductsDeliveryStepCategoryStatistic
} from "@/api/product";
import RenderLoading from "@/components/render-if-loading";
import { useParams } from "react-router-dom";
import { Card, Row, Col, Table, Tag, Progress, Divider, Empty, Tooltip } from 'antd';
import {
    TeamOutlined,
    ManOutlined,
    WomanOutlined,
    ShoppingOutlined,
    CheckCircleOutlined,
    PrinterOutlined,
    CarOutlined,
    RedoOutlined,
    BellOutlined,
    UserOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    SendOutlined,
    ReloadOutlined,
} from '@ant-design/icons';

// ---------- Helpers ----------
const ACTION_LABELS = {
    notif: 'اطلاع‌رسانی',
    print: 'چاپ',
    deliver: 'تحویل',
    reprint: 'چاپ مجدد',
    print_to_teacher: 'چاپ برای معلم',
    re_print_to_teacher: 'چاپ مجدد برای معلم',
    deliver_to_teacher: 'تحویل به معلم',
};

const ACTION_COLORS = {
    notif: '#1677ff',
    print: '#2f54eb',
    deliver: '#52c41a',
    reprint: '#fa8c16',
    print_to_teacher: '#722ed1',
    re_print_to_teacher: '#eb2f96',
    deliver_to_teacher: '#13c2c2',
};

const ACTION_ICONS = {
    notif: <BellOutlined />,
    print: <PrinterOutlined />,
    deliver: <CarOutlined />,
    reprint: <RedoOutlined />,
    print_to_teacher: <FileTextOutlined />,
    re_print_to_teacher: <ReloadOutlined />,
    deliver_to_teacher: <SendOutlined />,
};

const STATUS_LABELS = {
    delivered: 'تحویل شده',
    pending: 'در انتظار',
    print: 'چاپ شده',
    remove_lesson: 'حذف درس',
    refund: 'مرجوع شده',
};

const STATUS_COLORS = {
    delivered: 'green',
    pending: 'orange',
    print: 'blue',
    remove_lesson: 'red',
    refund: 'red',
};

const toPersianNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('fa-IR');
};

// ---------- Sub Components ----------
const SummaryCard = ({ title, value, icon, color, suffix }) => (
    <Card
        variant="borderless"
        className="shadow-sm hover:shadow-md transition-shadow rounded-2xl h-full"
    >
        <div className="flex items-center gap-4">
            <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: `${color}15`, color }}
            >
                {icon}
            </div>
            <div className="flex flex-col">
                <span className="  text-sm">{title}</span>
                <span className="text-2xl font-bold  ">
                    {toPersianNumber(value)}
                    {suffix && <span className="text-sm font-normal   mr-1">{suffix}</span>}
                </span>
            </div>
        </div>
    </Card>
);

const GenderSplit = ({ male = 0, female = 0, total = 0 }) => {
    const malePct = total ? Math.round((male / total) * 100) : 0;
    const femalePct = total ? Math.round((female / total) * 100) : 0;

    return (
        <Card
            title={<span className="font-bold">تفکیک جنسیت کاربران</span>}
            variant="borderless"
            className="shadow-sm rounded-2xl h-full"
        >
            <div className="space-y-6">
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="flex items-center gap-2 text-blue-600 font-medium">
                            <ManOutlined /> آقایان
                        </span>
                        <span className=" ">
                            {toPersianNumber(male)} نفر ({toPersianNumber(malePct)}٪)
                        </span>
                    </div>
                    <Progress
                        percent={malePct}
                        showInfo={false}
                        strokeColor="#1677ff"
                        trailColor="#f0f0f0"
                    />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="flex items-center gap-2 text-pink-500 font-medium">
                            <WomanOutlined /> خانم‌ها
                        </span>
                        <span className=" ">
                            {toPersianNumber(female)} نفر ({toPersianNumber(femalePct)}٪)
                        </span>
                    </div>
                    <Progress
                        percent={femalePct}
                        showInfo={false}
                        strokeColor="#eb2f96"
                        trailColor="#f0f0f0"
                    />
                </div>
            </div>
        </Card>
    );
};

const ProductsList = ({ products = [] }) => {
    if (!products || products.length === 0) {
        return (
            <Card
                title={<span className="font-bold">آمار محصولات</span>}
                variant="borderless"
                className="shadow-sm rounded-2xl h-full"
                extra={<ShoppingOutlined className=" " />}
            >
                <Empty description="محصولی برای نمایش وجود ندارد" />
            </Card>
        );
    }

    const maxCount = Math.max(...products.map((p) => p.count), 1);

    return (
        <Card
            title={<span className="font-bold">آمار محصولات</span>}
            variant="borderless"
            className="shadow-sm rounded-2xl h-full"
            extra={<ShoppingOutlined className=" " />}
        >
            <div className="space-y-5">
                {products.map((item) => {
                    const pct = Math.round((item.count / maxCount) * 100);
                    const isBoy = item?.product?.title?.includes('پسر');
                    const color = isBoy ? '#1677ff' : '#eb2f96';

                    return (
                        <div key={item.product_id}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="flex items-center gap-2   font-medium">
                                    <span
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: color }}
                                    />
                                    {item?.product?.title || 'نامشخص'}
                                </span>
                                <span className="  font-semibold">
                                    {toPersianNumber(item.count)}
                                </span>
                            </div>
                            <Progress
                                percent={pct}
                                showInfo={false}
                                strokeColor={color}
                                trailColor="#f0f0f0"
                                size="small"
                            />
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

// ---------- NEW: Products Status Breakdown ----------
const ProductsStatusBreakdown = ({ productsStatus = [] }) => {


    // گروه‌بندی بر اساس محصول
    const grouped = useMemo(() => {
        const map = new Map();
        productsStatus.forEach((item) => {
            const productId = item.product_id;
            if (!map.has(productId)) {
                map.set(productId, {
                    productId,
                    productTitle: item?.product?.title || 'نامشخص',
                    statuses: {},
                    total: 0,
                });
            }
            const group = map.get(productId);
            group.statuses[item.status] = (group.statuses[item.status] || 0) + item.count;
            group.total += item.count;
        });
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [productsStatus]);
    
    if (!productsStatus || productsStatus.length === 0) {
        return (
            <Card
                title={<span className="font-bold">محصولات به تفکیک وضعیت</span>}
                variant="borderless"
                className="shadow-sm rounded-2xl h-full"
                extra={<CheckCircleOutlined className=" " />}
            >
                <Empty description="داده‌ای برای نمایش وجود ندارد" />
            </Card>
        );
    }
    const statusOrder = ['delivered', 'print', 'pending', 'remove_lesson', 'refund'];

    return (
        <Card
            title={<span className="font-bold">محصولات به تفکیک وضعیت</span>}
            variant="borderless"
            className="shadow-sm rounded-2xl h-full"
            extra={<CheckCircleOutlined className=" " />}
        >
            <div className="space-y-6">
                {grouped.map((group) => {
                    const isBoy = group.productTitle.includes('پسر');
                    const baseColor = isBoy ? '#1677ff' : '#eb2f96';
                    const maxTotal = Math.max(...grouped.map((g) => g.total), 1);
                    const pct = Math.round((group.total / maxTotal) * 100);

                    return (
                        <div key={group.productId} className="border dark:border-gray-600 border-gray-100 rounded-xl p-4  /50">
                            {/* عنوان محصول */}
                            <div className="flex justify-between items-center mb-3">
                                <span className="flex items-center gap-2   font-semibold">
                                    <span
                                        className="w-2.5 h-2.5 rounded-full"
                                        style={{ backgroundColor: baseColor }}
                                    />
                                    {group.productTitle}
                                </span>
                                <span className="text-sm font-bold  ">
                                    {toPersianNumber(group.total)} مورد
                                </span>
                            </div>

                            {/* نوار پیشرفت کلی */}
                            <Progress
                                percent={pct}
                                showInfo={false}
                                strokeColor={baseColor}
                                trailColor="#e5e7eb"
                                size="small"
                                className="mb-3"
                            />

                            {/* تفکیک وضعیت‌ها */}
                            <div className="space-y-2">
                                {statusOrder.map((status) => {
                                    const count = group.statuses[status] || 0;
                                    if (count === 0) return null;
                                    const statusPct = group.total
                                        ? Math.round((count / group.total) * 100)
                                        : 0;

                                    return (
                                        <div
                                            key={status}
                                            className="flex items-center justify-between gap-3 text-sm"
                                        >
                                            <div className="flex items-center gap-2 min-w-[100px]">
                                                <Tag
                                                    color={STATUS_COLORS[status]}
                                                    className="rounded-full px-2 m-0"
                                                >
                                                    {STATUS_LABELS[status]}
                                                </Tag>
                                            </div>
                                            <div className="flex-1">
                                                <Progress
                                                    percent={statusPct}
                                                    showInfo={false}
                                                    strokeColor={
                                                        status === 'delivered'
                                                            ? '#52c41a'
                                                            : status === 'print'
                                                                ? '#1677ff'
                                                                : '#fa8c16'
                                                    }
                                                    trailColor="#f0f0f0"
                                                    size="small"
                                                />
                                            </div>
                                            <span className="font-medium   min-w-[60px] text-left">
                                                {toPersianNumber(count)}
                                                <span className="text-xs   mr-1">
                                                    ({toPersianNumber(statusPct)}٪)
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

const StepsTimeline = ({ steps = [] }) => {
    if (!steps || steps.length === 0) {
        return (
            <Card
                title={<span className="font-bold">مراحل تحویل اقلام</span>}
                variant="borderless"
                className="shadow-sm rounded-2xl h-full"
                extra={<AppstoreOutlined className=" " />}
            >
                <Empty description="مرحله‌ای برای نمایش وجود ندارد" />
            </Card>
        );
    }

    const maxCount = Math.max(...steps.map((s) => s.count), 1);

    return (
        <Card
            title={<span className="font-bold">مراحل تحویل اقلام</span>}
            variant="borderless"
            className="shadow-sm rounded-2xl h-full"
            extra={<AppstoreOutlined className=" " />}
        >
            <div className="space-y-6">
                {steps.map((step, idx) => (
                    <div key={step.product_delivery_step_id} className="relative">
                        <div className="flex items-start gap-4">
                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                                    {toPersianNumber(idx + 1)}
                                </div>
                                {idx < steps.length - 1 && (
                                    <div className="w-px h-10 bg-gray-200 my-1" />
                                )}
                            </div>
                            <div className="flex-1 pt-1">
                                <p className="  font-medium mb-2 leading-6">
                                    {step?.product_delivery_step?.description || '-'}
                                </p>
                                <div className="flex items-center gap-3">
                                    <Tag color="indigo" className="rounded-full px-3">
                                        {toPersianNumber(step.count)} مورد
                                    </Tag>
                                    <Progress
                                        percent={Math.round((step.count / maxCount) * 100)}
                                        showInfo={false}
                                        strokeColor="#6366f1"
                                        trailColor="#f0f0f0"
                                        size="small"
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

// ---------- Main Component ----------
function ProductDeliveryCategoryStatistic() {
    const params = useParams();
    const { data: statistic, isPending } = useApiSearchAdminProductsDeliveryStepCategoryStatistic({
        category: params.category,
    });

    const data = statistic?.data?.data;

    // ---- Expert aggregation ----
    const expertRows = useMemo(() => {
        if (!data) return [];

        const map = new Map();
        (data.experts || []).forEach((exp) => {
            map.set(exp.id, {
                key: exp.id,
                id: exp.id,
                name: `${exp.firstname} ${exp.lastname}`,
                notif: 0,
                print: 0,
                deliver: 0,
                reprint: 0,
                print_to_teacher: 0,
                re_print_to_teacher: 0,
                deliver_to_teacher: 0,
                total: 0,
            });
        });

        (data.status_count || []).forEach((item) => {
            if (!map.has(item.expert_id)) {
                map.set(item.expert_id, {
                    key: item.expert_id,
                    id: item.expert_id,
                    name: `کارشناس ${toPersianNumber(item.expert_id)}`,
                    notif: 0,
                    print: 0,
                    deliver: 0,
                    reprint: 0,
                    print_to_teacher: 0,
                    re_print_to_teacher: 0,
                    deliver_to_teacher: 0,
                    total: 0,
                });
            }
            const row = map.get(item.expert_id);
            if (row[item.action] !== undefined) {
                row[item.action] += item.count;
            }
            row.total += item.count;
        });

        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [data]);

    const columns = [
        {
            title: 'کارشناس',
            dataIndex: 'name',
            key: 'name',
            responsive: ['md'],
            render: (text) => (
                <span className="flex items-center gap-2 font-medium  ">
                    <UserOutlined className=" " />
                    {text}
                </span>
            ),
        },
        {
            title: 'اطلاع‌رسانی',
            dataIndex: 'notif',
            key: 'notif',
            align: 'center',
            responsive: ['lg'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="blue" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: 'چاپ',
            dataIndex: 'print',
            key: 'print',
            align: 'center',
            responsive: ['lg'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="geekblue" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: 'تحویل',
            dataIndex: 'deliver',
            key: 'deliver',
            align: 'center',
            responsive: ['lg'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="green" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: 'چاپ مجدد',
            dataIndex: 'reprint',
            key: 'reprint',
            align: 'center',
            responsive: ['lg'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="orange" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        // --- اکشن‌های جدید ---
        {
            title: (
                <Tooltip title="چاپ برای معلم">
                    <span>چاپ معلم</span>
                </Tooltip>
            ),
            dataIndex: 'print_to_teacher',
            key: 'print_to_teacher',
            align: 'center',
            responsive: ['xl'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="purple" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: (
                <Tooltip title="چاپ مجدد برای معلم">
                    <span>چاپ مجدد معلم</span>
                </Tooltip>
            ),
            dataIndex: 're_print_to_teacher',
            key: 're_print_to_teacher',
            align: 'center',
            responsive: ['xl'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="magenta" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: (
                <Tooltip title="تحویل به معلم">
                    <span>تحویل معلم</span>
                </Tooltip>
            ),
            dataIndex: 'deliver_to_teacher',
            key: 'deliver_to_teacher',
            align: 'center',
            responsive: ['xl'],
            render: (v) =>
                v > 0 ? (
                    <Tag color="cyan" className="rounded-full px-3">
                        {toPersianNumber(v)}
                    </Tag>
                ) : (
                    <span className=" ">-</span>
                ),
        },
        {
            title: 'مجموع',
            dataIndex: 'total',
            key: 'total',
            align: 'center',
            responsive: ['md'],
            render: (v) => (
                <span className="font-bold text-indigo-600">
                    {toPersianNumber(v)}
                </span>
            ),
            sorter: (a, b) => a.total - b.total,
            defaultSortOrder: 'descend',
        },

        // Mobile
        {
            title: '',
            key: 'mobile',
            responsive: ['xs', 'sm'],
            render: (_, record) => (
                <div className="flex md:hidden flex-col gap-3 py-1">
                    <div className="flex items-center gap-2 pb-1">
                        <UserOutlined className=" " />
                        <span className="font-medium   dark: ">
                            {record.name}
                        </span>
                    </div>

                    {[
                        { label: 'اطلاع‌رسانی', value: record.notif, color: 'blue' },
                        { label: 'چاپ', value: record.print, color: 'geekblue' },
                        { label: 'تحویل', value: record.deliver, color: 'green' },
                        { label: 'چاپ مجدد', value: record.reprint, color: 'orange' },
                        { label: 'چاپ معلم', value: record.print_to_teacher, color: 'purple' },
                        { label: 'چاپ مجدد معلم', value: record.re_print_to_teacher, color: 'magenta' },
                        { label: 'تحویل معلم', value: record.deliver_to_teacher, color: 'cyan' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="flex items-center justify-between gap-3">
                            <span className="text-xs   dark: ">
                                {label}
                            </span>
                            <span>
                                {value > 0 ? (
                                    <Tag color={color} className="rounded-full px-3">
                                        {toPersianNumber(value)}
                                    </Tag>
                                ) : (
                                    <span className=" ">-</span>
                                )}
                            </span>
                        </div>
                    ))}

                    <div className="flex items-center justify-between gap-3 pt-1">
                        <span className="text-xs   dark: ">
                            مجموع
                        </span>
                        <span className="font-bold text-indigo-600">
                            {toPersianNumber(record.total)}
                        </span>
                    </div>
                </div>
            ),
        },
    ];

    const totalActions = (data?.status_count || []).reduce((sum, i) => sum + i.count, 0);
    const totalProducts = data?.total_product;

    // محاسبه مجموع وضعیت‌ها برای کارت خلاصه
    const statusTotals = useMemo(() => {
        const totals = { delivered: 0, pending: 0, print: 0 };
        (data?.products_status_count || []).forEach((item) => {
            if (totals[item.status] !== undefined) {
                totals[item.status] += item.count;
            }
        });
        return totals;
    }, [data]);

    return (
        <AdminLayout
            backTo={URLS.admin.products.delivery.list}
            pageTitle={'آمار تجمیعی تحویل اقلام'}
        >
            <RenderLoading loading={isPending}>
                {!data ? (
                    <Empty description="داده‌ای برای نمایش وجود ندارد" className="mt-20" />
                ) : (
                    <div className="p-4 md:p-6   min-h-screen" dir="rtl">
                        {/* Header */}
                        <div className="mb-6">
                            <p className="  text-sm mt-1">
                                گزارش جامع کاربران، محصولات و فعالیت کارشناسان
                            </p>
                        </div>

                        {/* Summary Cards */}
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24} sm={12} lg={6}>
                                <SummaryCard
                                    title="کل کاربران"
                                    value={data.total_user}
                                    icon={<TeamOutlined />}
                                    color="#6366f1"
                                    suffix="نفر"
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <SummaryCard
                                    title="آقایان"
                                    value={data.total_male}
                                    icon={<ManOutlined />}
                                    color="#1677ff"
                                    suffix="نفر"
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <SummaryCard
                                    title="خانم‌ها"
                                    value={data.total_female}
                                    icon={<WomanOutlined />}
                                    color="#eb2f96"
                                    suffix="نفر"
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <SummaryCard
                                    title="مجموع اقلام"
                                    value={totalProducts}
                                    icon={<ShoppingOutlined />}
                                    color="#10b981"
                                    suffix="مورد"
                                />
                            </Col>
                        </Row>

                        {/* Second Row */}
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24} lg={12}>
                                <GenderSplit
                                    male={data.total_male}
                                    female={data.total_female}
                                    total={data.total_user}
                                />
                            </Col>
                            <Col xs={24} lg={12}>
                                <ProductsList products={data.products_count || []} />
                            </Col>
                        </Row>

                        {/* NEW: Products Status Breakdown Row */}
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24} lg={12}>
                                <ProductsStatusBreakdown
                                    productsStatus={data.products_status_count || []}
                                />
                            </Col>
                            <Col xs={24} lg={12}>
                                <StepsTimeline steps={data.steps || []} />
                            </Col>
                        </Row>

                        {/* Summary + Expert Table */}
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24}>
                                <Card
                                    title={<span className="font-bold">خلاصه فعالیت‌ها</span>}
                                    variant="borderless"
                                    className="shadow-sm rounded-2xl"
                                >
                                    <Row gutter={[16, 16]}>
                                        {Object.keys(ACTION_LABELS).map((action) => {
                                            const count = (data.status_count || [])
                                                .filter((i) => i.action === action)
                                                .reduce((sum, i) => sum + i.count, 0);
                                            return (
                                                <Col xs={12} sm={8} md={6} lg={4} key={action}>
                                                    <div className="p-3 rounded-xl   border dark:border-gray-600 border-gray-100">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span
                                                                className="text-base"
                                                                style={{ color: ACTION_COLORS[action] }}
                                                            >
                                                                {ACTION_ICONS[action]}
                                                            </span>
                                                            <span className="  text-xs">
                                                                {ACTION_LABELS[action]}
                                                            </span>
                                                        </div>
                                                        <div className="text-lg font-bold  ">
                                                            {toPersianNumber(count)}
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                        <Col xs={24}>
                                            <Divider className="my-2" />
                                            <div className="flex justify-between items-center px-2">
                                                <span className="  font-medium">
                                                    مجموع کل فعالیت‌ها
                                                </span>
                                                <span className="text-lg font-bold text-indigo-600">
                                                    {toPersianNumber(totalActions)}
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        {/* Expert Table */}
                        <Card
                            title={
                                <span className="font-bold flex items-center gap-2">
                                    <CheckCircleOutlined className="text-indigo-500" />
                                    عملکرد کارشناسان
                                </span>
                            }
                            variant="borderless"
                            className="shadow-sm rounded-2xl"
                            extra={
                                <span className="  text-sm">
                                    {toPersianNumber(expertRows.length)} کارشناس
                                </span>
                            }
                        >
                            <Table
                                columns={columns}
                                dataSource={expertRows}
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                className="[&_.ant-table-thead>tr>th]:  [&_.ant-table-thead>tr>th]: "
                            />
                        </Card>
                    </div>
                )}
            </RenderLoading>
        </AdminLayout>
    );
}

export default ProductDeliveryCategoryStatistic;